/**
 * Type definitions for nested objects in Case
 */
export interface CaseBranch {
  id: number;
  name: string;
  description?: string;
  icon_key?: string | null;
  updated_by?: number;
  [key: string]: any;
}

export interface CaseCountry {
  id: number;
  name: string;
  updated_by?: number;
  is_deleted?: number;
  deleted_at?: string | null;
  [key: string]: any;
}

export interface CaseRiskLevel {
  id: number;
  slug: string;
  name: string;
  iconKey?: string;
  prefix?: string;
  [key: string]: any;
}

export interface CaseStage {
  id: number;
  slug: string;
  name: string;
  updatedAt?: string;
  [key: string]: any;
}

export interface CaseRole {
  id: number;
  slug: string;
  name: string;
  updatedAt?: string;
  [key: string]: any;
}

export interface CaseProduct {
  id: number;
  name: string;
  vendor?: string;
  version?: string;
  version_details?: string;
  [key: string]: any;
}

export interface CaseKeyContactUser {
  id: number;
  keycloakUserId: string;
  name: string;
  [key: string]: any;
}

/**
 * Case class matching the new API response structure
 * Contains only fields that are returned from the API
 */
export class Case {
  // Core identification fields
  public id: number = 0;
  public internalId: string = "";
  public client: string = "";
  public title: string = "";

  // Description fields
  public brief: string = "";
  public description: string = "";
  public scenarioDescription: string | null = null;
  public explanation: string | null = null;

  // Versioning
  public version: number = 0;
  public major: number = 0;
  public minor: number = 0;

  // Location and organization
  public branch: CaseBranch = {} as CaseBranch;
  public branchId: number = 0;
  public country: CaseCountry = {} as CaseCountry;
  public countryId: number = 0;

  // AI-related fields
  public algorithms: any[] = [];
  public operations: any[] = [];
  public areas: any[] = [];
  public techniques: any[] = [];
  public machineLearning: any[] = [];

  // Role and product
  public role: CaseRole = {} as CaseRole;
  public roleId: number = 0;
  public roleUpdatedAt: string | null = null;
  public product: CaseProduct = {} as CaseProduct;
  public productId: number = 0;

  // Risk and stage
  public riskLevel: CaseRiskLevel = {} as CaseRiskLevel;
  public riskLevelId: number = 0;
  public riskLevelUpdatedAt: string | null = null;
  public stage: CaseStage = {} as CaseStage;
  public stageId: number = 0;
  public stageUpdatedAt: string | null = null;
  public stageKey: string = "";
  public stageColor: string = "";
  public stageIcon: string = "";

  // Users and contacts
  public userId: number = 0;
  public keyContact: string = "";
  public keyContactUser: CaseKeyContactUser = {} as CaseKeyContactUser;

  // Tasks and questions
  public tasks: number = 0;
  public completedTasks: number = 0;
  public totalQuestions: number = 0;
  public answeredQuestions: number = 0;

  // Links
  public link: string | null = null;

  // Status and workflow fields
  public caseStatusId: number = 0;
  public workflowCompleted: boolean = false;
  public published: number = 0;
  public publishedAt: string | null = null;

  // Update tracking
  public updatedBy: number | null = null;

  // Dates
  public createdAt: string = "";
  public updatedAt: string = "";
}

export class CaseDetailsForm {
  public title: string = "";
  public brief: string = "";
  public description: string = "";
  public scenarioDescription: string = "";
  public link: string = "";
  public version: string = "";

  public algorithms: any[] = [];
  public algorithmsIds: number[] = [];

  public operations: any[] = [];
  public operationsIds: number[] = [];

  public areas: any[] = [];
  public areasIds: number[] = [];

  public techniques: any[] = [];
  public techniquesIds: number[] = [];

  public machineLearning: any[] = [];
  public machineLearningIds: number[] = [];
}
